import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { FilterRequest, ProductAttributeDefinitionResponse, AttributeDataType, ProductAttributeDefinitionTranslationResponse, ProductAttributeDefinitionsService, AttributeRequest, ProductAttributeValueResponse, ProductAttributeValueTranslationResponse, ProductCustomisationRequest, ProductCustomisationsService, ProductsService } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { SessionService } from '@app/shared/services/session.service';

type ProductCustomisationRequestWithAttributes = ProductCustomisationRequest & {
  productAttributes?: AttributeRequest[];
};

@Component({
    selector: 'app-add-product-customizations',
    templateUrl: './add-product-customizations.component.html',
    styleUrl: './add-product-customizations.component.scss',
    host: {
        class: 'app-add-product-customizations'
    },
    standalone: false
})
export class AddProductCustomizationsComponent extends BaseViewComponent implements OnInit {

  canViewPrice: boolean = false;

  productAttributesFeatureEnabled: boolean = false;
  productAttributeDefinitions: ProductAttributeDefinitionResponse[] = [];

  comboData: ComboData = new ComboData(async (filter) => await firstValueFrom(this.productsService.apiProductsFilterComboPost(filter)));

  public productCustomisationsForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    productId: [[], [Validators.required]],
    description: [null, [Validators.required]],
    price: [null],
    imageUrl: [null],
    code: [null, [Validators.required]],
    comment: [null],
    productAttributes: this.fb.array([])
  });

  constructor(
    private router: Router,
    private productsService: ProductsService,
    private productCustomisationsService: ProductCustomisationsService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
    private session: SessionService,
    private fb: FormBuilder
  ) { super(); }

  async ngOnInit() {
    await this.init();
  }

  get productAttributesArray(): FormArray {
    return this.productCustomisationsForm.get('productAttributes') as FormArray;
  }

  private async init(): Promise<void> {

    const company = await this.session.getCompanyData();
    this.canViewPrice = company.hasPrice ?? false;

    await this.initComboData();

    const settings = await this.session.getCompanySettings();

    this.productAttributesFeatureEnabled = settings.features?.some(feature => feature.name === 'ProductAttributes' && feature.isEnabled) ?? false;

    if (this.productAttributesFeatureEnabled) {
      await this.loadProductAttributeDefinitions();
      this.buildDynamicProductAttributes();
    } else {
      this.productAttributeDefinitions = [];
      this.productAttributesArray.clear();
    }
  }

  private async initComboData() {
    this.comboData.additionalFilters = [
      {
        propertyName: 'isCustomisable',
        operator: FilterOperator.EQUALS,
        values: ['true']
      }
    ];

    await this.comboData.getData(null, 1);
  }

  async onLazyLoadComboData(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.comboData.getData(search, activePage);
  }

  getComboTotalPages(): number {
    const totalRecords = this.comboData?.totalRecords ?? 0;
    const pageSize = Number(this.comboData?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  onProductChange(event: any) {
    const selectedOption = event.selected;

    if (!selectedOption) {
      const productIdControl = this.productCustomisationsForm.get('productId');
      productIdControl?.reset();
      productIdControl?.markAsTouched();
    }
  }

  private async loadProductAttributeDefinitions(): Promise<void> {
    if (!this.productAttributesFeatureEnabled) {
      this.productAttributeDefinitions = [];
      this.productAttributesArray.clear();
      return;
    }

    const filter: FilterRequest = {
      pageSize: 500,
      pageNumber: 1,
      filters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
    };

    this.productAttributeDefinitions =
      (await firstValueFrom(
        this.productAttributeDefinitionsService.apiProductAttributeDefinitionsFilterPost(filter)
      )).results ?? [];
  }

  private buildDynamicProductAttributes(): void {
    this.productAttributesArray.clear();

    // Build a dynamic form group per definition
    this.productAttributeDefinitions.forEach(definition => {
      this.productAttributesArray.push(this.createProductAttributeGroup(definition));
    });
  }

  private createProductAttributeGroup(definition: ProductAttributeDefinitionResponse): FormGroup {
    const isFixedValues = definition.productAttributeDataType === AttributeDataType.FixedValues;
    const isMultiNumeric = !isFixedValues && definition.allowMultipleValues;

    return this.fb.group({
      definitionId: [definition.id],
      code: [definition.definitionCode],
      productAttributeDataType: [definition.productAttributeDataType],
      allowMultipleValues: [definition.allowMultipleValues],
      value: isFixedValues ? this.fb.control([]) : this.createTextboxValueControl(definition.productAttributeDataType),
      values: this.fb.array(isMultiNumeric ? [this.createTextboxValueControl(definition.productAttributeDataType)] : [])
    });
  }

  private createTextboxValueControl(dataType: AttributeDataType) {
    if (dataType === AttributeDataType.FixedValues) {
      return;
    }

    const validators = []; // [Validators.required]

    if (dataType === AttributeDataType.Integer) {
      validators.push(Validators.pattern(/^-?\d+$/));
    }

    if (dataType === AttributeDataType.Decimal) {
      validators.push(Validators.pattern(/^-?\d+([.,]\d+)?$/));
    }

    return this.fb.control(null, validators);
  }

  getNumericValuesArray(attributeIndex: number): FormArray {
    return this.productAttributesArray.at(attributeIndex).get('values') as FormArray;
  }

  addNumericMultiValue(attributeIndex: number): void {

    const maxDynamicFields = 10;

    const attributeGroup = this.productAttributesArray.at(attributeIndex) as FormGroup;
    const valuesArray = this.getNumericValuesArray(attributeIndex);
    const dataType = attributeGroup.get('productAttributeDataType')?.value;

    if (valuesArray.length >= maxDynamicFields) {
      return;
    }

    valuesArray.push(this.createTextboxValueControl(dataType));
  }

  removeNumericMultiValue(attributeIndex: number, valueIndex: number): void {
    const valuesArray = this.getNumericValuesArray(attributeIndex);

    if (valuesArray.length <= 1) {
      return;
    }

    valuesArray.removeAt(valueIndex);
  }

  isFixedValuesAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.FixedValues;
  }

  isTextAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Text;
  }

  isIntegerAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Integer;
  }

  isDecimalAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Decimal;
  }

  allowMultipleValuesAttribute(attribute: AbstractControl | null): boolean {
    return !!attribute?.get('allowMultipleValues')?.value;
  }

  getAttributeLabel(definition: ProductAttributeDefinitionResponse): string {
    // Use translation for current language
    const currentLang = this.session?.locale || 'en';
    const translations = definition?.productAttributeDefinitionTranslations || [];

    const currentTranslation = translations.find((item: ProductAttributeDefinitionTranslationResponse) => item.isoCode === currentLang);

    return currentTranslation?.translatedName || definition?.definitionCode || '';
  }

  getProductAttributeDropdownOptions(definition: ProductAttributeDefinitionResponse): Array<{ id: number; text: string }> {
    const currentLang = this.session?.locale || 'en';
    const values = definition?.productAttributeValues?.filter(x=> x.isActive) || [];

    // response values to dropdown options
    return values.map((item: ProductAttributeValueResponse) => {
      const translations = item.productAttributeValueTranslations || [];
      const currentTranslation = translations.find((translation: ProductAttributeValueTranslationResponse) => translation.isoCode === currentLang);

      return {
        id: item.id || 0,
        text: currentTranslation?.translatedValue || item.valueCode
      };
    });
  }

  async submitForm() {
    if (this.productCustomisationsForm.valid) {
      let dynamicProductAttributes: AttributeRequest[] = [];

      if (this.productAttributesFeatureEnabled) {
        dynamicProductAttributes = this.productAttributesArray.controls.map((productAttributeControl): AttributeRequest => {
            const productAttributeForm = productAttributeControl as FormGroup;

            const attributeDefinitionId = productAttributeForm.get('definitionId')?.value;
            const productAttributeDataType = productAttributeForm.get('productAttributeDataType')?.value;
            const productAttributeAllowsMultipleValues = !!productAttributeForm.get('allowMultipleValues')?.value;

            let productAttributeValues: any[];

            if (productAttributeDataType === AttributeDataType.FixedValues) {
              const selectedDropdownValues = productAttributeForm.get('value')?.value || [];

              productAttributeValues = Array.isArray(selectedDropdownValues) ? selectedDropdownValues : [selectedDropdownValues];
            } else if (productAttributeAllowsMultipleValues) {
              productAttributeValues = (productAttributeForm.get('values') as FormArray).value;
            } else {
              const singleInputValue = productAttributeForm.get('value')?.value;

              productAttributeValues = [singleInputValue];
            }

            const filledProductAttributeValues = productAttributeValues.filter(value =>
              value !== null &&
              value !== undefined &&
              value !== ''
            );

            return {
              attributeDefinitionId: attributeDefinitionId,
              values: filledProductAttributeValues
            };
          }).filter(productAttribute => {
            if (!productAttribute.values) {
              return false;
            }

            return productAttribute.values.length > 0;
          });
      }

      const payload: ProductCustomisationRequestWithAttributes = {
        name: this.productCustomisationsForm.controls['name'].value,
        description: this.productCustomisationsForm.controls['description'].value,
        price: this.productCustomisationsForm.controls['price'].value,
        imageUrl: this.productCustomisationsForm.controls['imageUrl'].value,
        code: this.productCustomisationsForm.controls['code'].value,
        comment: this.productCustomisationsForm.controls['comment'].value,
        productId: this.productCustomisationsForm.controls['productId'].value?.[0],
        productAttributes: dynamicProductAttributes
      };

      await firstValueFrom(this.productCustomisationsService.apiProductCustomisationsCreatePost(payload));

      this.productCustomisationsForm.controls['name'].setValue(null);
      this.productCustomisationsForm.controls['description'].setValue(null);
      this.productCustomisationsForm.controls['price'].setValue(null);
      this.productCustomisationsForm.controls['imageUrl'].setValue(null);
      this.productCustomisationsForm.controls['code'].setValue(null);
      this.productCustomisationsForm.controls['comment'].setValue(null);
      this.productCustomisationsForm.controls['productId'].setValue([]);
      this.productAttributesArray.clear();

      this.router.navigate(['/product-customisations']);
    }
  }
}
